import { pageTitle } from '@palaccount/config';
import type { CreditReason, FreeCreditLineInput } from '@palaccount/contracts';
import { Money } from '@palaccount/money';
import { Loader2, Plus, Send, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Amount } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { DocumentNumberChip } from '@/components/document-number-chip';
import { useUnsavedChanges } from '@/components/document-header-controls';
import { DocumentToolbar } from '@/components/document-toolbar';
import { useLineImport } from '@/features/documents/line-tools';
import { DocumentTotals, TwoWayPair } from '@/features/documents/document-totals';
import { DescriptionLangToggle, useDescriptionLanguage } from '@/features/documents/description-language';
import { Gated } from '@/components/permission-gate';
import { DocStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Typeahead } from '@/components/ui/typeahead';
import { useCashAccountOptions } from '@/features/accounts/api';
import { useOrgSetting } from '@/features/admin/api';
import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { DocumentActions } from '@/features/documents/document-actions';
import { FxPanel, useRateWarningGate } from '@/features/documents/fx-panel';
import { RateField, useRateInput } from '@/features/documents/rate-field';
import { DocCurrencyCell, useDocCurrency } from '@/features/documents/doc-currency';
import { creditNoteDocument } from '@/features/documents/record-documents';
import { ItemCell } from '@/features/inventory/item-cell';
import { LineUnitSelect } from '@/features/sales/sales-header-fields';
import { useItemPicker } from '@/features/inventory/item-picker';
import { useWarehouses } from '@/features/inventory/stock-api';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';
import { cn } from '@/lib/utils';

import { useInvoicePicker } from './api';
import { ApplyCreditNoteDialog, CreditApplicationsCard } from './apply-credit-dialog';
import { CellInput } from './return-editor-page';
import { isReturnEditable, trimQty } from './return-caps';
import {
  useCreateCreditNote,
  useCreditNote,
  useDeleteCreditNote,
  useItemHistory,
  usePostCreditNote,
  useRefundCreditNote,
  useUpdateCreditNote,
} from './returns-api';
import { computeTotalsMirror, type TwoWayState } from './totals-mirror';
import {
  captureBlocks,
  textFromStored,
  trackingPayload,
  type CaptureText,
  type TrackedItem,
} from './tracking-capture';
import { TrackingRows } from './tracking-rows';

const REASONS: CreditReason[] = [
  'damaged_goods',
  'wrong_item_shipped',
  'customer_changed_mind',
  'overstock',
  'other',
];

/** One free line of a stand-alone return (RET-02). */
interface FreeRow {
  key: number;
  itemId: string;
  /** Wave 4C: the unit the qty is typed in; '' = the item's stock unit. */
  unitId: string;
  warehouseId: string;
  description: string;
  qty: string;
  unitPrice: string;
  discountPct: string;
  /** BR-MD-03-17 the other way round — see the invoice-backed editor. */
  tracking: CaptureText;
  /**
   * Wave 1B — whether the clerk has TYPED the price. While false, the
   * customer's last sale price for the item (`useItemHistory`) may replace
   * the catalogue default once it arrives; a typed price, or a stored
   * line's price, is never overwritten by a lookup that lands later.
   */
  priceTouched: boolean;
}

let nextKey = 1;
const blankRow = (): FreeRow => ({
  key: nextKey++,
  itemId: '',
  unitId: '',
  warehouseId: '',
  description: '',
  qty: '',
  unitPrice: '',
  discountPct: '',
  tracking: {},
  priceTouched: false,
});

/** `{pct|amount}` for the wire from the two-way box, or nothing when empty. */
function twoWayBody(state: TwoWayState | undefined): { pct: string } | { amount: string } | undefined {
  const value = state?.value.trim() ?? '';
  if (!state || value === '') return undefined;
  return state.basis === 'pct' ? { pct: value } : { amount: value };
}

/**
 * Wave 1B — the per-line history hint. Its own component because it holds a
 * query per line (hooks cannot sit inside the rows' map): it asks what THIS
 * customer bought and returned of THIS item, hands the last sale price up to
 * the row while the clerk has not typed one, and warns — never blocks — when
 * the quantity coming back is more than the customer ever bought. A
 * stand-alone return has no invoice to cap against (RET-02), so the figure is
 * a hint beside the box, not a refusal.
 */
function LineHistory({
  partnerId,
  row,
  currency,
  editable,
  onDefaultPrice,
}: {
  partnerId: string;
  row: FreeRow;
  currency: string;
  editable: boolean;
  onDefaultPrice: (key: number, price: string) => void;
}) {
  const { t } = useTranslation();
  const { data: history } = useItemHistory(partnerId, row.itemId);
  const lastPrice =
    history?.lastPrice !== null && history?.lastPrice !== undefined && history.lastPriceCurrency === currency
      ? history.lastPrice
      : null;

  useEffect(() => {
    // Settles after one pass: once the row holds the last price (or the clerk
    // has typed one) the guard is false and nothing is written again.
    if (editable && !row.priceTouched && lastPrice !== null && row.unitPrice !== lastPrice) {
      onDefaultPrice(row.key, lastPrice);
    }
  }, [lastPrice, row.key, row.unitPrice, row.priceTouched, editable, onDefaultPrice]);

  if (!history) return null;
  // Quantities, not money: Number() is the comparison DECISION.md §2.1 allows
  // for a hint, and the server re-reads the same figures on post.
  const qty = Number(row.qty);
  const available = Number(history.qtySold) - Number(history.qtyReturned);
  const overSold = Number.isFinite(qty) && qty > 0 && qty > available;
  if (!overSold) return null;
  return (
    <p
      role="status"
      className="mt-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
    >
      {t('returns.overSoldHint', {
        sold: trimQty(history.qtySold),
        returned: trimQty(history.qtyReturned),
      })}
    </p>
  );
}

/**
 * RET-02 — the STAND-ALONE sales return: a credit note with no source
 * invoice. The customer brought something back and nobody can point at the
 * invoice it was sold on, so the return names the customer itself and FREE
 * lines: an item (the picker), a quantity, a unit price defaulting to the
 * item's sale price and editable, and a warehouse per line like the invoice.
 *
 * Nothing here is capped — there is no billed quantity to cap against — and
 * nothing is copied from an invoice, which is exactly why this is its own
 * component beside `return-editor-page.tsx` rather than a mode inside it:
 * that editor's every column is a cap figure or a copied price, and the
 * invoice-backed rules (BR-SAL-05-03/-04/-06) stay untouched over there.
 * Posting (sales.approve) credits the receivable, restocks at the cost the
 * customer's goods last left at (else the current average), and taxes at the
 * company default unless the VAT box is typed — the invoice's own
 * arithmetic, run by the server, with F-2's document discount when typed.
 *
 * Wave 1B adds the door BACK to the invoice-backed shape: a "Return against
 * an invoice" picker over the chosen customer's POSTED invoices
 * (BR-SAL-05-02) that navigates to `?invoiceId=` — the route then draws the
 * other editor with the invoice's lines loaded and capped. And each line
 * asks the customer's history for the item: the last sale price fills the
 * unit price ahead of the catalogue price, and a quantity above what they
 * bought (less what they returned) draws a warning, not a wall.
 */
export function StandaloneReturnEditor({ creditNoteId }: { creditNoteId: string | undefined }) {
  const { t, i18n } = useTranslation();
  // The reader's language is the PDF's; `print.language` may override it in
  // the template (INV-H6), for the worker and the browser alike.
  const printLocale = i18n.language.startsWith('ar') ? ('ar' as const) : ('en' as const);
  const navigate = useNavigate();
  const isNew = !creditNoteId;

  const { data: creditNote, isPending: loadingNote } = useCreditNote(creditNoteId);
  const createCreditNote = useCreateCreditNote();
  const updateCreditNote = useUpdateCreditNote(creditNoteId ?? '');
  const deleteCreditNote = useDeleteCreditNote(creditNoteId ?? '');
  const postCreditNote = usePostCreditNote(creditNoteId ?? '');
  const refundCreditNote = useRefundCreditNote(creditNoteId ?? '');
  const { options: cashOptions } = useCashAccountOptions();
  const [refundAccountId, setRefundAccountId] = useState('');
  /** Wave 4A — the "Apply to invoices" box is open. */
  const [applying, setApplying] = useState(false);
  /** Wave 4A — Post return's one option: walk the credit onto open invoices, oldest first. */
  const [applyOnPost, setApplyOnPost] = useState(false);

  const canEdit = useCan(isNew ? 'sales.create' : 'sales.edit');
  const canPost = useCan('sales.approve');
  const canEditNotes = useCan('sales.edit');

  const [creditDate, setCreditDate] = useDocumentDate();
  const [partnerId, setPartnerId] = useState('');
  const [reason, setReason] = useState<CreditReason>('customer_changed_mind');
  const [reasonNote, setReasonNote] = useState('');
  const [restock, setRestock] = useState(true);
  const [rows, setRows] = useState<FreeRow[]>(() => [blankRow()]);
  // EC-19-73: the VAT box opens at the COMPANY default rate; typed, it is
  // sent as the document's own box (BR-SAL-00-14 — settings never reach back).
  const defaultTaxPct = useOrgSetting<string>('invoicing.default_vat_pct');
  const [taxPct, setTaxPct] = useState<string | null>(null);
  // F-2 — the document discount pair, two views of one number (wave 1B).
  const [discount, setDiscount] = useState<TwoWayState | undefined>(undefined);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { confirmRateWarning, rateWarningDialog } = useRateWarningGate();
  const loadedId = useRef<string | null>(null);
  /** S4 — the header rail's ‹‹ ›› wait while there is typed work (see `useUnsavedChanges`). */
  const dirty = useUnsavedChanges(
    [creditDate, partnerId, reason, reasonNote, restock, rows, taxPct, discount, notes],
    isNew ? 'new' : creditNote && loadedId.current === creditNote.id ? creditNote.id : null,
  );

  // GEN-50 — two typeaheads: the customer, and the items these rows name.
  const customerPicker = usePartnerPicker(partnerId || null, { type: 'customer' });
  const itemPicker = useItemPicker(
    useMemo(() => rows.map((row) => row.itemId), [rows]),
    { status: 'active' },
  );
  const { data: warehouses } = useWarehouses();
  const activeWarehouses = (warehouses ?? []).filter((warehouse) => warehouse.isActive);
  // The door back to the invoice-backed shape: THAT customer's POSTED
  // invoices only (BR-SAL-05-02), searched on the server, off until a
  // customer is chosen. Nothing is held — picking one leaves this editor.
  const invoicePicker = useInvoicePicker(
    null,
    partnerId ? { partnerId, status: 'posted' } : { status: 'posted' },
    isNew && partnerId !== '',
  );

  const status = creditNote?.status ?? 'draft';
  const editable = isReturnEditable(status) && canEdit;
  const partner = customerPicker.byId.get(partnerId);
  // GEN-108's chain: the document's own code, else the customer's, else base.
  const base = useBaseCurrency();
  // Wave 4D — the editable rate (features/documents/rate-field).
  const rate = useRateInput(creditNote);
  // Per-document currency (2026-10-30): the header's dropdown in front of the chain.
  const docCurrency = useDocCurrency({
    partnerId,
    savedCurrency: creditNote?.currency,
    partnerCurrency: partner?.currency ?? base,
    onPick: () => rate.setText(''),
  });
  const currency = docCurrency.currency;

  useEffect(() => {
    document.title = pageTitle(t('returns.editorTitle'));
  }, [t]);

  // An existing note: adopt the stored document.
  useEffect(() => {
    if (creditNote && loadedId.current !== creditNote.id) {
      loadedId.current = creditNote.id;
      setCreditDate(creditNote.creditDate);
      setPartnerId(creditNote.partnerId);
      setReason(creditNote.reason);
      setReasonNote(creditNote.reasonNote ?? '');
      setRestock(creditNote.restock);
      setNotes(creditNote.notes ?? '');
      setTaxPct(creditNote.taxPct.replace(/\.00$/, ''));
      // The stored block says which discount box was typed only by which
      // figure is non-zero; a zero pair is no discount at all.
      setDiscount(
        creditNote.discountPct !== '0.00'
          ? { basis: 'pct', value: creditNote.discountPct.replace(/\.00$/, '') }
          : creditNote.discountAmount !== '0.00'
            ? { basis: 'amount', value: creditNote.discountAmount }
            : undefined,
      );
      setRows(
        creditNote.lines.map((line) => ({
          key: nextKey++,
          itemId: line.itemId ?? '',
          unitId: line.unitId ?? '',
          warehouseId: line.warehouseId ?? '',
          description: line.description,
          qty: trimQty(line.qty),
          unitPrice: line.unitPrice,
          discountPct: line.discountPct.replace(/\.00$/, ''),
          tracking: textFromStored(line),
          // A stored price is the document's own — no lookup overwrites it.
          priceTouched: true,
        })),
      );
    }
  }, [creditNote]);

  const effectiveTaxPct = taxPct ?? defaultTaxPct;
  const totals = useMemo(
    () =>
      computeTotalsMirror(
        rows.map((row) => ({ qty: row.qty, unitPrice: row.unitPrice, discountPct: row.discountPct })),
        discount,
        { basis: 'pct', value: effectiveTaxPct },
        currency,
        defaultTaxPct,
      ),
    [rows, discount, effectiveTaxPct, currency, defaultTaxPct],
  );

  const setRow = (key: number, patch: Partial<FreeRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  /**
   * The picker's item fills what the clerk has not typed: its name, its sale
   * price. The customer's LAST price for it, when there is one, replaces the
   * catalogue figure a moment later (`LineHistory`) — unless typed first.
   */
  const pickItem = (key: number, itemId: string) => {
    if (!itemId) {
      setRow(key, { itemId: '', unitId: '', warehouseId: '', unitPrice: '', tracking: {}, priceTouched: false });
      return;
    }
    const item = itemPicker.byId.get(itemId);
    setRow(key, {
      itemId,
      // The picked unit belonged to the OLD item.
      unitId: '',
      description: item ? descLang.describe(item) : '',
      unitPrice: item ? item.unitPrice : '',
      tracking: {},
      priceTouched: false,
    });
  };

  const defaultPrice = (key: number, price: string) =>
    setRows((current) =>
      current.map((row) =>
        row.key === key && !row.priceTouched ? { ...row, unitPrice: price } : row,
      ),
    );

  // BR-SAL-00-20: removing the last line leaves one blank line.
  const removeRow = (key: number) =>
    setRows((current) => {
      const left = current.filter((row) => row.key !== key);
      return left.length === 0 ? [blankRow()] : left;
    });

  const blocksFor = (row: FreeRow) =>
    captureBlocks(itemPicker.byId.get(row.itemId) as TrackedItem | undefined, row.qty);

  const requestLines: FreeCreditLineInput[] = useMemo(
    () =>
      rows
        .filter((row) => row.itemId && /^\d+(\.\d+)?$/.test(row.qty.trim()) && Number(row.qty) > 0)
        .map((row) => ({
          itemId: row.itemId,
          ...(row.unitId ? { unitId: row.unitId } : {}),
          ...(row.warehouseId ? { warehouseId: row.warehouseId } : {}),
          ...(row.description.trim() ? { description: row.description.trim() } : {}),
          qty: row.qty.trim(),
          ...(row.unitPrice.trim() ? { unitPrice: row.unitPrice.trim() } : {}),
          ...(row.discountPct.trim() ? { discountPct: row.discountPct.trim() } : {}),
          // Damaged goods restock nothing (BR-SAL-05-07), so nothing is named.
          ...(restock ? trackingPayload(blocksFor(row), row.tracking) : {}),
        })),
    // `blocksFor` closes over the picker's items, which is why they are listed.
    [rows, restock, itemPicker.byId],
  );

  const validate = (): string | null => {
    if (!partnerId) return t('returns.errPickCustomer');
    if (requestLines.length === 0) return t('returns.errNoLines');
    if (reason === 'other' && reasonNote.trim() === '') return t('returns.errOtherNeedsNote');
    return null;
  };

  const handleFailure = (
    thrown: unknown,
    permission: 'sales.create' | 'sales.edit' | 'sales.delete' | 'sales.approve',
  ) => {
    if (thrown instanceof ApiError && thrown.status === 403) denyPermission(permission);
    setError(localizeServerCause(t, thrown, 'returns.errSaveFailed'));
  };

  /** Wave 4C — lines from a small .xlsx/.csv; a stand-alone return credits goods, so every row needs a known item code. */
  /** Wave 4C — which name a picked/imported item writes into a NEW line description. */
  const descLang = useDescriptionLanguage();
  const lineImport = useLineImport({
    id: 'credit-note',
    side: 'sales',
    describe: descLang.describe,
    copyLines: () =>
      rows
        .filter((line) => line.itemId !== '' || line.description.trim() !== '')
        .map((line) => ({
          code: itemPicker.byId.get(line.itemId)?.code ?? '',
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
        })),
    requireItem: true,
    visible: isReturnEditable(status),
    gate: {
      permission: isNew ? 'sales.create' : 'sales.edit',
      reasonKey: isNew ? 'perm.cannot.createSalesReturns' : 'perm.cannot.editSalesReturns',
    },
    disabled: busy || !editable,
    onLines: (imported) =>
      setRows((current) => [
        ...current.filter((row) => row.itemId !== '' || row.qty.trim() !== ''),
        ...imported.map((line) => ({
          ...blankRow(),
          itemId: line.itemId,
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
          // The file's figures are committed; no later lookup replaces them.
          priceTouched: true,
        })),
      ]),
  });

  const save = async (navigateAfter = true): Promise<string | null> => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return null;
    }
    const tax = effectiveTaxPct.trim() === '' ? undefined : { pct: effectiveTaxPct.trim() };
    // F-2: the typed box rides as typed; a cleared box on a saved draft sends
    // an explicit zero, because the server keeps the stored discount unless
    // re-typed (BR-SAL-00-14) and "cleared" IS a re-type.
    const discountBody = twoWayBody(discount) ?? (isNew ? undefined : { pct: '0' });
    try {
      if (isNew) {
        const created = await createCreditNote.mutateAsync({
          ...rate.createBody(),
          ...docCurrency.body(),
          partnerId,
          creditDate,
          reason,
          ...(reasonNote.trim() ? { reasonNote: reasonNote.trim() } : {}),
          restock,
          lines: requestLines,
          ...(discountBody ? { discount: discountBody } : {}),
          ...(tax ? { tax } : {}),
          ...(notes.trim() ? { notes } : {}),
        });
        if (navigateAfter) void navigate(`/sales/returns/${created.id}`, { replace: true });
        return created.id;
      }
      await updateCreditNote.mutateAsync({
        ...rate.patchBody(),
        ...docCurrency.body(),
        creditDate,
        reason,
        reasonNote: reasonNote.trim() === '' ? null : reasonNote.trim(),
        restock,
        lines: requestLines,
        ...(discountBody ? { discount: discountBody } : {}),
        ...(tax ? { tax } : {}),
        notes: notes.trim() === '' ? null : notes,
      });
      loadedId.current = null;
      return creditNoteId!;
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'sales.create' : 'sales.edit');
      return null;
    }
  };

  const onSave = async () => {
    setBusy(true);
    await save();
    setBusy(false);
  };

  /** Post return — saves first, then posts (same shape as the invoice-backed editor). */
  const onPost = async () => {
    if (!(await confirmRateWarning(creditNote?.rateWarning))) return;
    setBusy(true);
    setError(null);
    try {
      const id = await save(false);
      if (!id) return;
      if (isNew) {
        const { api } = await import('@/api');
        const response = await api.creditNotes.post({
          params: { creditNoteId: id },
          body: { applyToInvoices: applyOnPost },
        });
        if (response.status !== 200) {
          if (response.status === 403) denyPermission('sales.approve');
          const body = response.body;
          const sentence =
            typeof body === 'object' && body !== null && 'message' in body
              ? String((body as { message: unknown }).message)
              : t('common.loadFailed');
          setError(localizeServerMessage(t, sentence, new ApiError(response.status, sentence, body)));
          return;
        }
        void navigate(`/sales/returns/${id}`, { replace: true });
        return;
      }
      await postCreditNote.mutateAsync({ applyToInvoices: applyOnPost });
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'sales.approve');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setDeleting(false);
    if (!creditNoteId) return;
    setBusy(true);
    setError(null);
    try {
      await deleteCreditNote.mutateAsync();
      void navigate('/sales/returns');
    } catch (thrown) {
      handleFailure(thrown, 'sales.delete');
    } finally {
      setBusy(false);
    }
  };

  const onSaveNotes = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateCreditNote.mutateAsync({ notes: notes.trim() === '' ? null : notes });
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'sales.edit');
    } finally {
      setBusy(false);
    }
  };

  const onRefund = async () => {
    if (!creditNoteId || !refundAccountId) return;
    setBusy(true);
    setError(null);
    try {
      await refundCreditNote.mutateAsync(refundAccountId);
      loadedId.current = null;
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403)
        denyPermission('accounting.approve');
      setError(localizeServerCause(t, thrown, 'returns.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loadingNote) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  // A saved note's stored block is authoritative; a new one shows the mirror.
  // Either way the "after discount" figure (F-3's taxable base) is the
  // subtraction, drawn once by DocumentTotals rather than stored twice.
  const shown = creditNote
    ? {
        subtotal: creditNote.subtotal,
        discountAmount: creditNote.discountAmount,
        taxAmount: creditNote.taxAmount,
        grandTotal: creditNote.grandTotal,
      }
    : {
        subtotal: totals.subtotal,
        discountAmount: totals.discountAmount ?? '0.00',
        taxAmount: totals.taxAmount ?? '0.00',
        grandTotal: totals.grandTotal ?? totals.subtotal,
      };

  return (
    <div className="grid gap-4">
      {rateWarningDialog}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('returns.editorTitle')}</h2>
        <DocumentNumberChip
          label={t('returns.colNo')}
          code={creditNote?.code ?? null}
          fallback={t('returns.codeNew')}
        />
        <DocStatusChip status={status} />
        {/* The strip's source pill says so: there is no invoice behind this one. */}
        <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
          {t('returns.noInvoice')}
        </span>
        <DocumentToolbar
          header={{ kind: 'creditNote', id: creditNoteId, dirty }}
          actions={[
            {
              // A link back to the list. It wore the word "Cancel" beside
              // Delete, which read as PART 4.6's Cancel row (the act that
              // reverses a posted document) — "Close" says what it does.
              id: 'back',
              row: null,
              label: t('common.close'),
              variant: 'outline',
              to: '/sales/returns',
            },
            {
              // Wave 2 — the invoice's Print/PDF pair on the stand-alone
              // credit note; the same `credit_note` paper, with no invoice
              // code on it. Saved notes only.
              id: 'printPdf',
              row: 'print',
              node:
                !isNew && creditNote ? (
                  <DocumentActions
                    request={{ kind: 'credit_note', locale: printLocale, subjectId: creditNote.id }}
                    payload={{ kind: 'credit_note', data: creditNoteDocument(creditNote) }}
                  />
                ) : null,
            },
            // Wave 4C — Import lines / Download template (shared line tools).
            ...lineImport.acts,
            {
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && status === 'draft',
              gate: { permission: 'sales.delete', reasonKey: 'perm.cannot.deleteSalesReturns' },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: isReturnEditable(status),
              gate: {
                permission: isNew ? 'sales.create' : 'sales.edit',
                reasonKey: isNew
                  ? 'perm.cannot.createSalesReturns'
                  : 'perm.cannot.editSalesReturns',
              },
              busy,
              onAct: () => void onSave(),
            },
            {
              // Wave 4A — set the posted credit against the customer's open
              // invoices; offered while unapplied credit stands.
              id: 'apply',
              row: null,
              label: t('returns.applyToInvoices'),
              variant: 'outline',
              visible:
                !isNew &&
                status === 'posted' &&
                creditNote !== undefined &&
                creditNote.refundedAt === null &&
                Money.of(creditNote.unappliedTotal, creditNote.currency).isPositive(),
              gate: { permission: 'sales.approve', reasonKey: 'perm.cannot.postSalesReturns' },
              disabled: busy,
              onAct: () => setApplying(true),
            },
          ]}
        >
          {creditNote ? (
            <ApplyCreditNoteDialog creditNote={creditNote} open={applying} onClose={() => setApplying(false)} />
          ) : null}
          {!isNew && status === 'draft' ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('returns.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
        </DocumentToolbar>
      </div>

      {status === 'posted' ? (
        <p className="rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          {t('returns.postedNotice')}
        </p>
      ) : null}
      {status === 'posted' && !creditNote?.refundedAt ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          <span className="me-auto">{t('returns.refundHint')}</span>
          <NativeSelect
            aria-label={t('returns.refundAccount')}
            value={refundAccountId}
            onChange={(event) => setRefundAccountId(event.target.value)}
            className="w-auto"
          >
            <option value="">{t('receipts.pickAccount')}</option>
            {cashOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.code} — {option.name}
              </option>
            ))}
          </NativeSelect>
          <Gated permission="accounting.approve" reasonKey="perm.cannot.refundCreditNotes">
            <Button size="sm" variant="outline" onClick={() => void onRefund()} disabled={busy || !refundAccountId}>
              {t('returns.refund')}
            </Button>
          </Gated>
        </div>
      ) : null}
      {creditNote?.refundedAt ? (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {t('returns.refundedNotice')}
        </p>
      ) : null}
      {/* Wave 4A: the full credit is applied — nothing left to refund or apply. */}
      {status === 'credited' ? (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {t('returns.creditedNotice')}
        </p>
      ) : null}
      {creditNote ? <CreditApplicationsCard creditNote={creditNote} /> : null}
      {/* Wave 4C — the line import's chooser, summary and per-row problems. */}
      {lineImport.panel}
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      {/* ── The details line: date, customer, reason, restock ───────────── */}
      <Card>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="grid gap-1.5">
            <Label htmlFor="cn-date">{t('common.date')}</Label>
            <Input
              id="cn-date"
              type="date"
              dir="ltr"
              value={creditDate}
              onChange={(event) => setCreditDate(event.target.value)}
              disabled={!editable}
            />
          </div>
          {/* Per-document currency (2026-10-30): a draft credit note may pick its own. */}
          <DocCurrencyCell
            idPrefix="cn"
            value={currency}
            onChange={docCurrency.pick}
            disabled={!editable || status !== 'draft'}
          />
          {/* Wave 4D — the editable rate (features/documents/rate-field). */}
          <RateField idPrefix="cn" currency={currency} doc={creditNote} input={rate} disabled={!editable} />
          <div className="grid gap-1.5">
            {isNew ? (
              <>
                {/* The visible label the new form lacked (wave 1B); the
                    typeahead carries the same words as its accessible name. */}
                <Label htmlFor="cn-customer">{t('sales.fCustomer')}</Label>
                <PartnerTypeahead
                  entity="customer"
                  id="cn-customer"
                  label={t('sales.fCustomer')}
                  value={partnerId || null}
                  onChange={(id) => setPartnerId(id ?? '')}
                  options={customerPicker.options}
                  selected={customerPicker.optionFor(partnerId)}
                  query={customerPicker.query}
                  onQueryChange={customerPicker.setQuery}
                  placeholder={t('sales.pickCustomer')}
                  emptyLabel={t('picker.noMatchingCustomer')}
                  loading={customerPicker.loading}
                />
              </>
            ) : (
              <>
                <Label>{t('sales.fCustomer')}</Label>
                <div className="flex h-12 items-center gap-3 rounded-[11px] border border-input bg-muted/40 px-3 text-sm">
                  <span className="truncate font-medium">{creditNote?.partnerName ?? ''}</span>
                  <span className="ms-auto font-mono text-muted-foreground">{currency}</span>
                </div>
              </>
            )}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="cn-reason">{t('returns.fReason')}</Label>
            <NativeSelect
              id="cn-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value as CreditReason)}
              disabled={!editable}
            >
              {REASONS.map((value) => (
                <option key={value} value={value}>
                  {t(`returns.reason.${value}`)}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="cn-restock">{t('returns.fRestock')}</Label>
            <label className="flex h-12 items-center gap-3 rounded-[11px] border border-input px-3 text-sm">
              <Checkbox
                id="cn-restock"
                checked={restock}
                onCheckedChange={(checked) => setRestock(checked === true)}
                disabled={!editable}
              />
              <span className="text-muted-foreground">{t('returns.restockAverageHint')}</span>
            </label>
          </div>
          {reason === 'other' ? (
            <div className="grid gap-1.5 sm:col-span-2 lg:col-span-4">
              <Label htmlFor="cn-reason-note">{t('returns.fReasonNote')}</Label>
              <Input
                id="cn-reason-note"
                value={reasonNote}
                maxLength={500}
                placeholder={t('returns.reasonNotePh')}
                onChange={(event) => setReasonNote(event.target.value)}
                disabled={!editable}
              />
            </div>
          ) : null}
          {isNew ? (
            <div className="grid gap-1.5 sm:col-span-2">
              {/* Wave 1B — the door to the invoice-backed shape: pick one of
                  THIS customer's posted invoices and the route redraws the
                  other editor with its lines loaded and capped. Nothing typed
                  here is kept: a return against an invoice copies the
                  invoice (BR-SAL-05-04), it does not merge with a draft. */}
              <Label htmlFor="cn-source-invoice">{t('returns.fSourceInvoice')}</Label>
              <Typeahead
                id="cn-source-invoice"
                label={t('returns.fSourceInvoice')}
                value={null}
                onChange={(id) => {
                  if (id) void navigate(`/sales/returns/new?invoiceId=${id}`);
                }}
                options={invoicePicker.options}
                selected={null}
                query={invoicePicker.query}
                onQueryChange={invoicePicker.setQuery}
                placeholder={t('returns.pickInvoicePh')}
                emptyLabel={t('picker.noMatchingInvoice')}
                loading={invoicePicker.loading}
                disabled={partnerId === ''}
              />
              <p className="text-xs text-muted-foreground">{t('returns.pickInvoiceHint')}</p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* ── The free-line grid ───────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('returns.linesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-11 text-center">#</TableHead>
                <TableHead className="min-w-[150px]">{t('quotes.colItem')}</TableHead>
                <TableHead className="w-24">{t('sales.colUnit')}</TableHead>
                <TableHead className="min-w-[130px]">{t('orders.colWarehouse')}</TableHead>
                <TableHead className="min-w-[200px]">{t('sales.colDescription')}
                  {editable ? <DescriptionLangToggle language={descLang} /> : null}
                </TableHead>
                <TableHead className="w-24 text-end">{t('sales.colQty')}</TableHead>
                <TableHead className="w-32 text-end">{t('sales.colUnitPrice')}</TableHead>
                <TableHead className="w-24 text-end">{t('sales.colDisc')}</TableHead>
                <TableHead className="w-32 text-end">{t('sales.colTotal')}</TableHead>
                {editable ? <TableHead className="w-11" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, index) => {
                const view = totals.lines[index];
                return (
                  <React.Fragment key={row.key}>
                    <TableRow className="odd:bg-muted/30">
                      <TableCell className="text-center font-mono text-muted-foreground">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        {/* A stand-alone return credits GOODS, so every line names an item: no free-text row. */}
                        <ItemCell
                          label={t('quotes.cellItem', { line: index + 1 })}
                          picker={itemPicker}
                          value={row.itemId}
                          onPick={(itemId) => pickItem(row.key, itemId)}
                          disabled={!editable}
                        />
                        {partnerId && row.itemId ? (
                          <LineHistory
                            partnerId={partnerId}
                            row={row}
                            currency={currency}
                            editable={editable}
                            onDefaultPrice={defaultPrice}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {/* Wave 4C — the unit the qty is typed in; '' = the item's stock unit. */}
                        <LineUnitSelect
                          item={row.itemId ? itemPicker.byId.get(row.itemId) : undefined}
                          value={row.unitId}
                          onChange={(unitId) => setRow(row.key, { unitId })}
                          disabled={!editable}
                          label={t('sales.cellUnit', { line: index + 1 })}
                        />
                      </TableCell>
                      <TableCell>
                        {row.itemId ? (
                          <NativeSelect
                            aria-label={t('orders.cellWarehouse', { line: index + 1 })}
                            className="h-9 text-sm"
                            value={row.warehouseId}
                            onChange={(event) => setRow(row.key, { warehouseId: event.target.value })}
                            disabled={!editable}
                          >
                            <option value="">{t('sales.pickWarehouse')}</option>
                            {activeWarehouses.map((warehouse) => (
                              <option key={warehouse.id} value={warehouse.id}>
                                {warehouse.name}
                              </option>
                            ))}
                          </NativeSelect>
                        ) : (
                          <span className="px-2 text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <CellInput
                          aria-label={t('sales.cellDescription', { line: index + 1 })}
                          value={row.description}
                          maxLength={200}
                          onChange={(event) => setRow(row.key, { description: event.target.value })}
                          disabled={!editable}
                        />
                      </TableCell>
                      <TableCell>
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellQty', { line: index + 1 })}
                          value={row.qty}
                          placeholder="0"
                          onChange={(event) => setRow(row.key, { qty: event.target.value })}
                          disabled={!editable}
                        />
                      </TableCell>
                      <TableCell>
                        {/* Defaults to the customer's last price for the item,
                            else the item's sale price — and editable. */}
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellUnitPrice', { line: index + 1 })}
                          value={row.unitPrice}
                          placeholder="0.00"
                          onChange={(event) =>
                            setRow(row.key, { unitPrice: event.target.value, priceTouched: true })
                          }
                          disabled={!editable}
                        />
                      </TableCell>
                      <TableCell>
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellDisc', { line: index + 1 })}
                          value={row.discountPct}
                          placeholder="0"
                          onChange={(event) => setRow(row.key, { discountPct: event.target.value })}
                          disabled={!editable}
                        />
                      </TableCell>
                      <TableCell className="text-end">
                        {view?.lineTotal ? (
                          <Amount value={view.lineTotal} currency={currency} />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      {editable ? (
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t('sales.removeLine', { line: index + 1 })}
                            title={t('sales.removeLine', { line: index + 1 })}
                            onClick={() => removeRow(row.key)}
                          >
                            <Trash2 aria-hidden />
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                    {restock && row.itemId ? (
                      <TrackingRows
                        blocks={blocksFor(row)}
                        text={row.tracking}
                        onChange={(key, value) =>
                          setRow(row.key, { tracking: { ...row.tracking, [key]: value } })
                        }
                        disabled={!editable}
                        columns={editable ? 10 : 9}
                        line={index + 1}
                      />
                    ) : null}
                  </React.Fragment>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={editable ? 10 : 9} className="text-end text-sm">
                  <span className="text-muted-foreground">{t('orders.netTaxTotal')}</span>{' '}
                  <Amount value={shown.subtotal} currency={currency} /> {' / '}
                  <Amount value={shown.taxAmount} currency={currency} /> {' / '}
                  <Amount value={shown.grandTotal} currency={currency} className="font-bold" />
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
          {editable ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => setRows((current) => [...current, blankRow()])}
            >
              <Plus aria-hidden /> {t('sales.addLine')}
            </Button>
          ) : null}
          <p className="mt-2 text-xs text-muted-foreground">{t('returns.standaloneHint')}</p>
        </CardContent>
      </Card>

      {/* ── Notes + the posting rail ─────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader>
            <CardTitle>{t('sales.notes')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Textarea
              aria-label={t('sales.notes')}
              maxLength={2000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={!editable && !(status === 'posted' && canEditNotes)}
            />
            {status === 'posted' ? (
              <Gated permission="sales.edit" reasonKey="perm.cannot.editNotes" className="justify-self-start">
                <Button variant="outline" onClick={() => void onSaveNotes()} disabled={busy}>
                  {t('sales.saveNotes')}
                </Button>
              </Gated>
            ) : null}
          </CardContent>
        </Card>

        <Card className="self-start">
          <CardContent className="grid gap-3">
            {/* Wave 4C — the shared totals rows; the VAT stays this screen's single % box. */}
            <DocumentTotals
              currency={currency}
              subtotal={shown.subtotal}
              discountAmount={shown.discountAmount}
              discountControl={
                <TwoWayPair
                  kind="discount"
                  state={discount}
                  onChange={setDiscount}
                  pct={totals.discountPct ?? '0.00'}
                  amount={shown.discountAmount ?? ''}
                  disabled={!editable}
                />
              }
              taxAmount={shown.taxAmount}
              vatControl={
                <span className="flex items-center gap-2">
                  <Input
                    aria-label={t('sales.vat')}
                    dir="ltr"
                    inputMode="decimal"
                    className={cn('h-8 w-20 text-end font-mono', !editable && 'opacity-70')}
                    value={effectiveTaxPct}
                    onChange={(event) => setTaxPct(event.target.value)}
                    disabled={!editable}
                  />
                  <span className="text-muted-foreground">%</span>
                  <Amount value={shown.taxAmount} currency={currency} />
                </span>
              }
              grandTotal={shown.grandTotal}
              totalLabel={t('returns.creditTotal')}
            />

            <FxPanel doc={creditNote} />

            {/* Wave 4A — Post return's one option (RET-02 only: a return
                against an invoice already nets that invoice). */}
            {status === 'draft' ? (
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={applyOnPost}
                  onCheckedChange={(checked) => setApplyOnPost(checked === true)}
                  className="mt-0.5"
                />
                <span>{t('returns.applyOnPost')}</span>
              </label>
            ) : null}
            {status === 'draft' ? (
              <Gated permission="sales.approve" reasonKey="perm.cannot.postSalesReturns" className="w-full">
                <Button
                  className="h-[46px] w-full bg-emerald-700 text-white hover:bg-emerald-800"
                  onClick={() => void onPost()}
                  disabled={busy || requestLines.length === 0}
                >
                  {busy ? <Loader2 className="animate-spin" aria-hidden /> : <Send aria-hidden />}
                  {t('returns.postReturn')}
                </Button>
              </Gated>
            ) : null}
            {status === 'draft' && !canPost ? (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('returns.approvalBanner')}
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
      {/* Wave 2 — INV-H5's files panel on this document. Hidden
          behind "save first" on a new draft; permission follows the record. */}
      <AttachmentsPanel kind="credit_note" entityId={creditNoteId} />
    </div>
  );
}
